<template>
  <div v-if="visible" class="signup-progress-overlay">
    <v-card
      class="signup-progress-card"
      variant="flat"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-progress-title"
      aria-describedby="signup-progress-description"
    >
      <div class="signup-progress-heading">
        <AIcon icon="tabler:brand-meta" color="primary" size="22" with-container />
        <h2 id="signup-progress-title">{{ title }}</h2>
      </div>
      <p id="signup-progress-description" class="signup-progress-description">{{ description }}</p>
      <div class="signup-steps">
        <div v-for="step in steps" :key="step.key" class="signup-step">
          <span
            class="signup-step-indicator"
            :class="`is-${step.status}`"
            role="img"
            :aria-label="step.status.replace('_', ' ')"
          ></span>
          <div class="signup-step-copy">
            <strong>{{ step.title }}</strong>
            <p>{{ step.description }}</p>
          </div>
        </div>
      </div>
      <p v-if="errorMessage" class="signup-error-message">
        {{ errorMessage }}
      </p>
      <div v-if="flowStatus === 'error' || flowStatus === 'success'" class="signup-progress-actions">
        <AButton variant="outlined" @click="$emit('close')">
          Close
        </AButton>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { AButton, AIcon } from '@adereso-ai/vue-components'
import type { SignupFlowStatus, SignupStep } from '@/composables/useEmbeddedSignup'

interface Props {
  visible: boolean
  title: string
  description: string
  steps: SignupStep[]
  errorMessage: string
  flowStatus: SignupFlowStatus
}

defineProps<Props>()

defineEmits<{
  close: []
}>()
</script>

<style scoped lang="scss">
.signup-progress-overlay {
  align-items: center;
  background-color: rgba(0, 0, 0, 0.58);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 24px;
  position: fixed;
  z-index: 1000;
}

.signup-progress-card {
  background: var(--bsp-surface) !important;
  color: var(--bsp-text) !important;
  border: 1px solid var(--bsp-border) !important;
  border-radius: var(--bsp-radius-md) !important;
  box-shadow: var(--bsp-shadow-hover) !important;
  max-width: 560px;
  padding: 24px;
  width: 100%;

  h2 {
    margin: 0;
    color: var(--bsp-text);
    font-size: 20px;
    line-height: 1.35;
  }
}

.signup-progress-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.signup-progress-description {
  color: var(--bsp-text-muted);
  margin-bottom: 20px;
  line-height: 1.55;
}

.signup-steps {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.signup-step {
  align-items: flex-start;
  display: flex;
  gap: 12px;
}

.signup-step-indicator {
  border-radius: 999px;
  flex-shrink: 0;
  height: 12px;
  margin-top: 5px;
  width: 12px;

  &.is-pending {
    background-color: var(--bsp-border);
  }

  &.is-in_progress {
    background-color: var(--bsp-brand);
  }

  &.is-completed {
    background-color: var(--bsp-success);
  }

  &.is-skipped {
    background-color: var(--bsp-warning);
  }

  &.is-failed {
    background-color: var(--bsp-error);
  }
}

.signup-step-copy {
  color: var(--bsp-text);

  p {
    color: var(--bsp-text-muted);
    margin: 4px 0 0;
    line-height: 1.45;
  }
}

.signup-error-message {
  color: var(--bsp-error);
  margin: 18px 0 0;
}

.signup-progress-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

@media (max-width: 640px) {
  .signup-progress-overlay {
    align-items: flex-end;
    padding: 12px;
  }

  .signup-progress-card {
    padding: 20px;
  }
}
</style>
